import React, { useState, useEffect, useMemo } from 'react';
import EvidenceGalleryModal from '../../custom_modules/EvidenceGallaryModal/EvidenceGalleryModal';
import useAudioDurations from '../../hooks/useAudioDurations';
import useEvidenceTransform from './useEvidenceTransform';
import EvidenceDisplaySkeleton from './EvidenceDisplaySkeleton';
import {
  EvidenceIconSection,
  EvidenceIcon,
  MoreBadge,
  EmptyStateText,
  AudioEvidenceWrapper,
  AudioDurationText,
} from './EvidenceDisplay.styles';
import { SIZE_PRESETS, DEFAULT_CONFIG, EMPTY_STATE } from './constants';
import audioIcon from '../../assets/img/icons/audio-gallery-primary.svg';
import { CommonImage } from 'styledComponents/common';

/**
 * EvidenceDisplay
 * Modular, reusable component for displaying evidence (images/audio)
 * Displays max 2 items (preferring 1 image + 1 audio), shows "+N" badge
 * Manages gallery modal internally for seamless interaction
 *
 * @component
 * @example
 * // Basic usage
 * <EvidenceDisplay evidence={assignee?.evidences} size="md" />
 *
 * @example
 * // With custom sizing
 * <EvidenceDisplay evidence={job?.evidence} width="36px" height="36px" />
 *
 * @param {object|array} evidence - Single evidence object or array of evidence objects
 * @param {function} onEvidenceClick - Optional callback when evidence item is clicked
 * @param {string} size - Size preset: 'sm' | 'md' | 'lg' (default: 'md')
 * @param {string} width - Optional override for icon width (e.g., '24px', '36px')
 * @param {string} height - Optional override for icon height (e.g., '24px', '36px')
 * @param {string} gap - Optional override for gap between icons
 * @param {boolean} showGallery - Show gallery modal on click (default: true)
 * @param {string} audioIconSrc - Optional custom audio icon source
 * @param {function} getDuration - Optional function to get audio duration
 * @param {boolean} showAudioDuration - Show duration text for audio items (default: true)
 * @param {boolean} isLoading - Show skeleton state (default: false)
 * @param {function} onError - Optional error callback
 * @returns {JSX.Element}
 */
const EvidenceDisplay = ({
  evidence,
  onEvidenceClick,
  size = DEFAULT_CONFIG.size,
  width,
  height,
  gap,
  showGallery = DEFAULT_CONFIG.showGallery,
  audioIconSrc = audioIcon,
  getDuration,
  showAudioDuration = true,
  isLoading = DEFAULT_CONFIG.isLoading,
  onError,
}) => {
  // State management
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);

  // Get size preset
  const preset = SIZE_PRESETS[size] || SIZE_PRESETS.md;
  const finalWidth = width || preset.width;
  const finalHeight = height || preset.height;
  const finalGap = gap || preset.gap;
  const finalBorderRadius = preset.borderRadius;

  // Transform evidence data
  const { visibleItems, remainingCount, allItems, totalCount, hasMore } =
    useEvidenceTransform(evidence);

  // Extract audio URLs for duration fetching
  const audioUrls = useMemo(() => {
    return allItems
      .filter((item) => item.type === 'audio')
      .map((item) => item.src);
  }, [allItems]);

  // Get audio durations using custom hook
  const { getDuration: getAudioDuration } =
    useAudioDurations(audioUrls);

  // Use provided getDuration or default
  const finalGetDuration = getDuration || getAudioDuration;

  // Transform items to EvidenceGalleryModal format
  const galleryItemsFormat = useMemo(
    () =>
      allItems.map((item) => ({
        type: item.type,
        src: item.src,
        label: item.label,
      })),
    [allItems]
  );

  // Handle evidence item click
  const handleItemClick = (item, index) => {
    if (onEvidenceClick) {
      onEvidenceClick(item);
    }

    if (showGallery) {
      setGalleryItems(galleryItemsFormat);
      setGalleryStartIndex(index);
      setGalleryOpen(true);
    }
  };

  // Handle "more" badge click
  const handleMoreClick = () => {
    if (showGallery) {
      setGalleryItems(galleryItemsFormat);
      // Start from first hidden item (after visible 2)
      setGalleryStartIndex(visibleItems.length);
      setGalleryOpen(true);
    }
  };

  // Handle gallery close
  const handleCloseGallery = () => {
    setGalleryOpen(false);
    setGalleryItems([]);
    setGalleryStartIndex(0);
  };

  // Empty state - show only if no evidence
  if (totalCount === 0) {
    return <EmptyStateText>{EMPTY_STATE}</EmptyStateText>;
  }

  return (
    <>
      <EvidenceIconSection gap={finalGap}>
        {visibleItems.map((item, index) => (
          item.type === 'audio' ? (
            <AudioEvidenceWrapper
              key={`${item.id}-${index}`}
              width={finalWidth}
              height={finalHeight}
              borderRadius={finalBorderRadius}
              onClick={() =>
                handleItemClick(item, item.allItemsIndex)
              }
              aria-label={`Evidence ${item.type}`}
              title={`Audio: ${item.label}`}
            >
              <CommonImage style={{ marginTop: '4px' }} src={audioIconSrc} alt="Audio evidence" />
              {showAudioDuration && finalGetDuration && (
                <AudioDurationText>
                  {finalGetDuration(item.src)}
                </AudioDurationText>
              )}
            </AudioEvidenceWrapper>
          ) : (
            <EvidenceIcon
              key={`${item.id}-${index}`}
              width={finalWidth}
              height={finalHeight}
              borderRadius={finalBorderRadius}
              onClick={() =>
                handleItemClick(item, item.allItemsIndex)
              }
              aria-label={`Evidence ${item.type}`}
              title={`Image: ${item.label}`}
            >
              <CommonImage src={item.src} alt={item.label} />
            </EvidenceIcon>
          )
        ))}

        {hasMore && (
          <MoreBadge
            height={finalHeight}
            onClick={handleMoreClick}
            aria-label={`${remainingCount} more evidence items`}
            title={`Show ${remainingCount} more evidence items`}
          >
            +{remainingCount}
          </MoreBadge>
        )}
      </EvidenceIconSection>

      {showGallery && (
        <EvidenceGalleryModal
          isOpen={galleryOpen}
          items={galleryItems}
          startIndex={galleryStartIndex}
          onClose={handleCloseGallery}
        />
      )}
    </>
  );
};

export default EvidenceDisplay;
